import {
  CopyOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  FilterOutlined,
  LinkedinOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload
} from 'antd';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RecordingPlayer from '../Recording/RecordingPlayer';
import AddCandidateModal from './AddCandidateModal';
import EvaluateCandidateModal from './EvaluateCandidateModal';
import SendCandidateCallModal from './SendCandidateCallModal';
import SendCandidateEmailModal from './SendCandidateEmailModal';
import SendCandidateWhatsAppModal from './SendCandidateWhatsAppModal';
import PlacementJourneyPanel from './PlacementJourneyPanel';

const { Title, Text } = Typography;
const { Option } = Select;

const getStoredAuthToken = () => {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('firebaseToken') ||
    localStorage.getItem('idToken');
  if (!token) return '';
  const normalized = String(token).trim();
  if (!normalized || ['null', 'undefined'].includes(normalized.toLowerCase())) {
    return '';
  }
  return normalized;
};

const CandidateSearch = () => {
  const navigate = useNavigate();
  /**
   * Add-to-list only for tenant-private rows. Search API already scopes private docs with
   * Firestore client_id == token tenant; shared pool uses is_universal == true.
   * Do not re-match client_id in the UI — JWT / userProfile often disagree with resume.client_id.
   */
  const resumeMatchesTenant = (record) => {
    const u = record.is_universal;
    if (u === true || u === 1 || u === 'true') return false;
    return true;
  };

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    title: '',
    industry: '',
    position: '',
    location: '',
    min_score: '',
    max_score: '',
    // New AI tag filters
    ai_skills: [],
    ai_industries: [],
    ai_experience_level: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  // Cursor-based pagination (Option B backend). Cursor for page N means "start_after" values for that page.
  const [pageCursors, setPageCursors] = useState({ 1: null });
  const debounceTimerRef = useRef(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  // const [advancedSearchVisible, setAdvancedSearchVisible] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailCandidate, setEmailCandidate] = useState(null);
  const [callModalVisible, setCallModalVisible] = useState(false);
  const [callCandidate, setCallCandidate] = useState(null);
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false);
  const [whatsappCandidate, setWhatsappCandidate] = useState(null);
  const [addCandidateModalVisible, setAddCandidateModalVisible] = useState(false);
  const [evaluateModalVisible, setEvaluateModalVisible] = useState(false);
  const [evaluateCandidate, setEvaluateCandidate] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [addListModalVisible, setAddListModalVisible] = useState(false);
  const [candidateListsForAdd, setCandidateListsForAdd] = useState([]);
  const [addToListTargetId, setAddToListTargetId] = useState(undefined);
  const [newListNameForAdd, setNewListNameForAdd] = useState('');
  const [addListSubmitting, setAddListSubmitting] = useState(false);
  const queryParams = new URLSearchParams(window.location.search || '');
  const requestedReturnTo = queryParams.get('return_to') || '';
  const candidateListReturnTo = requestedReturnTo === '/candidate-lists'
    || requestedReturnTo.startsWith('/candidate-lists/')
    ? requestedReturnTo
    : '';

  const closeCandidateProfile = () => {
    setProfileModalVisible(false);
    if (candidateListReturnTo) navigate(candidateListReturnTo);
  };

  // Deep-link support: /all-candidates?candidate_id=<id>
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search || '');
    const candidateId = qs.get('candidate_id');
    if (!candidateId) return;

    // Only attempt if we have an auth token available (dev env may not)
    const token = getStoredAuthToken();
    if (!token) return;

    axios
      .get(`/api/candidates/${encodeURIComponent(candidateId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.success && res.data?.candidate) {
          viewCandidateProfile(res.data.candidate);
        }
      })
      .catch(() => {
        // No-op: deep-link is best-effort, UI still works normally.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Predefined options for tag filters
  const skillOptions = [
    // Finance & Banking Skills
    { value: 'Trade Finance', label: 'Trade Finance' },
    { value: 'Investment Banking', label: 'Investment Banking' },
    { value: 'Financial Modeling', label: 'Financial Modeling' },
    { value: 'Corporate Finance', label: 'Corporate Finance' },
    { value: 'Commercial Banking', label: 'Commercial Banking' },
    { value: 'Private Equity', label: 'Private Equity' },
    { value: 'Asset Management', label: 'Asset Management' },
    { value: 'Credit Analysis', label: 'Credit Analysis' },
    { value: 'Mergers & Acquisitions', label: 'Mergers & Acquisitions' },
    { value: 'Compliance', label: 'Compliance' },
    { value: 'Audit', label: 'Audit' },

    // Trading & Risk Management
    { value: 'Commodity Trading', label: 'Commodity Trading' },
    { value: 'Oil Trading', label: 'Oil Trading' },
    { value: 'Gas Trading', label: 'Gas Trading' },
    { value: 'Metals Trading', label: 'Metals Trading' },
    { value: 'Precious Metals Trading', label: 'Precious Metals Trading' },
    { value: 'Gold Trading', label: 'Gold Trading' },
    { value: 'Agricultural Trading', label: 'Agricultural Trading' },
    { value: 'Hedging', label: 'Hedging' },
    { value: 'Derivatives', label: 'Derivatives' },
    { value: 'Futures', label: 'Futures' },
    { value: 'Options', label: 'Options' },
    { value: 'Var', label: 'VaR (Value at Risk)' },
    { value: 'Risk Management', label: 'Risk Management' },
    { value: 'Stress Testing', label: 'Stress Testing' },

    // Supply Chain & Logistics
    { value: 'Supply Chain Finance', label: 'Supply Chain Finance' },
    { value: 'Supply Chain Management', label: 'Supply Chain Management' },
    { value: 'Logistics Management', label: 'Logistics Management' },
    { value: 'Warehouse Management', label: 'Warehouse Management' },
    { value: 'Inventory Management', label: 'Inventory Management' },
    { value: 'Freight Forwarding', label: 'Freight Forwarding' },
    { value: 'Customs Clearance', label: 'Customs Clearance' },

    // Food & Beverage & Hospitality
    { value: 'Food And Beverage', label: 'Food & Beverage' },
    { value: 'Restaurant Management', label: 'Restaurant Management' },
    { value: 'Hotel Management', label: 'Hotel Management' },
    { value: 'Hospitality Management', label: 'Hospitality Management' },
    { value: 'Catering', label: 'Catering' },
    { value: 'Food Safety', label: 'Food Safety' },
    { value: 'HACCP', label: 'HACCP' },
    { value: 'Food Quality Control', label: 'Food Quality Control' },
    { value: 'Culinary Arts', label: 'Culinary Arts' },

    // Technology Skills
    { value: 'Python', label: 'Python' },
    { value: 'JavaScript', label: 'JavaScript' },
    { value: 'Java', label: 'Java' },
    { value: 'SQL', label: 'SQL' },
    { value: 'React', label: 'React' },
    { value: 'Node.js', label: 'Node.js' },
    { value: 'AWS', label: 'AWS' },
    { value: 'Docker', label: 'Docker' },
    { value: 'Kubernetes', label: 'Kubernetes' },
    { value: 'R', label: 'R Programming' },
    { value: 'Go', label: 'Go Programming' },
    { value: 'Machine Learning', label: 'Machine Learning' },
    { value: 'Data Science', label: 'Data Science' },
    { value: 'Data Analysis', label: 'Data Analysis' },

    // Commodity Specific
    { value: 'Crude Oil', label: 'Crude Oil' },
    { value: 'Natural Gas', label: 'Natural Gas' },
    { value: 'Copper', label: 'Copper' },
    { value: 'Aluminum', label: 'Aluminum' },
    { value: 'Gold', label: 'Gold' },
    { value: 'Silver', label: 'Silver' },
    { value: 'Corn', label: 'Corn' },
    { value: 'Wheat', label: 'Wheat' },
    { value: 'Soybeans', label: 'Soybeans' },
    { value: 'Cotton', label: 'Cotton' },
    { value: 'Sugar', label: 'Sugar' },
    { value: 'Coffee', label: 'Coffee' },
    { value: 'Cocoa', label: 'Cocoa' },

    // Trading Strategies
    { value: 'Arbitrage', label: 'Arbitrage' },
    { value: 'Spread Trading', label: 'Spread Trading' },
    { value: 'Pairs Trading', label: 'Pairs Trading' },
    { value: 'Algorithmic Trading', label: 'Algorithmic Trading' },
    { value: 'Quantitative Trading', label: 'Quantitative Trading' },
    { value: 'High Frequency Trading', label: 'High Frequency Trading' },

    // Market Analysis
    { value: 'Technical Analysis', label: 'Technical Analysis' },
    { value: 'Fundamental Analysis', label: 'Fundamental Analysis' },
    { value: 'Supply & Demand Analysis', label: 'Supply & Demand Analysis' },
    { value: 'Market Structure Analysis', label: 'Market Structure Analysis' },
    { value: 'Geopolitical Analysis', label: 'Geopolitical Analysis' },
    { value: 'Weather Analysis', label: 'Weather Analysis' },
  ];

  const industryOptions = [
    // Core Industries
    { value: 'Finance', label: 'Finance' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Education', label: 'Education' },
    { value: 'Consulting', label: 'Consulting' },

    // Specialized Finance & Trading
    { value: 'Commodity Finance', label: 'Commodity Finance' },
    { value: 'Commodity Trading', label: 'Commodity Trading' },
    { value: 'Investment Banking', label: 'Investment Banking' },
    { value: 'Commercial Banking', label: 'Commercial Banking' },

    // Food & Beverage & Hospitality
    { value: 'F&B Hospitality', label: 'F&B & Hospitality' },
    { value: 'Food Processing', label: 'Food Processing' },

    // Energy & Resources
    { value: 'Energy', label: 'Energy' },
    { value: 'Mining & Metals', label: 'Mining & Metals' },
    { value: 'Agriculture', label: 'Agriculture' },

    // Logistics & Supply Chain
    { value: 'Logistics', label: 'Logistics' },

    // Other Industries
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'E-commerce', label: 'E-commerce' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Media', label: 'Media' },
  ];

  const experienceLevelOptions = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'lead', label: 'Lead' },
    { value: 'executive', label: 'Executive' },
  ];

  // Helper to safely coerce any value to an array for robust rendering
  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return [value];
    if (typeof value === 'string' && value.trim().length > 0) return [{ title: value }];
    return [];
  };

  // Search candidates
  const searchCandidates = async (searchFilters = filters, page = 1) => {
    try {
      setLoading(true);
      const token = getStoredAuthToken();
      const cursor = page === 1 ? null : (pageCursors?.[page] || null);

      const response = await axios.post('/api/candidates/search', {
        filters: searchFilters,
        pagination: { page, limit: pagination.limit, cursor },
        sort_by: 'contactName',
        sort_order: 'asc',
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const rows = response.data.candidates || [];
        const pg = response.data.pagination || {};
        const hasMore = !!pg.has_more;
        const nextCursor = pg.next_cursor || null;

        setCandidates(rows);

        // Store cursor for the next page (sequential navigation).
        setPageCursors((prev) => ({
          ...(prev || { 1: null }),
          ...(nextCursor ? { [page + 1]: nextCursor } : {}),
        }));

        // Ant Pagination needs a numeric "total". We use a minimal synthetic total
        // so Next is available only when backend reports has_more.
        const syntheticTotal =
          (page - 1) * pagination.limit + rows.length + (hasMore ? 1 : 0);

        setPagination({
          page,
          limit: pagination.limit,
          total: syntheticTotal,
          total_pages: null,
          has_more: hasMore,
          next_cursor: nextCursor,
        });
      } else {
        message.error('Failed to search candidates');
      }
    } catch (error) {
      console.error('Search error:', error);
      message.error(error.response?.data?.error || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Reset cursor pagination when filters change
    setPageCursors({ 1: null });
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Debounce text-like inputs to avoid firing on every keystroke.
    const debouncedKeys = new Set(['name', 'title', 'industry', 'position', 'location', 'min_score', 'max_score']);
    if (debouncedKeys.has(key)) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        searchCandidates(newFilters, 1);
      }, 400);
      return;
    }

    searchCandidates(newFilters, 1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setPagination({ ...pagination, page });
    searchCandidates(filters, page);
  };

  const handlePageSizeChange = (current, size) => {
    const newPagination = { ...pagination, page: 1, limit: size };
    setPagination(newPagination);
    setPageCursors({ 1: null });
    searchCandidates(filters, 1);
  };

  // View candidate profile (hydrate full document — search list uses trimmed fields only)
  const viewCandidateProfile = async (candidate) => {
    let merged = { ...candidate };
    const token = getStoredAuthToken();
    if (token && candidate?.id) {
      try {
        const res = await axios.get(
          `/api/candidates/${encodeURIComponent(candidate.id)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.success && res.data?.candidate) {
          merged = { ...candidate, ...res.data.candidate };
        }
      } catch (e) {
        console.warn('Profile hydrate skipped:', e?.message || e);
      }
    }
    // Ensure candidate has proper structure to prevent undefined errors
    const safeCandidate = {
      ...merged,
      // Handle both old 'title' and new 'job_title' fields
      job_title: merged.job_title || merged.title,
      lastEvaluatedJob: merged.lastEvaluatedJob || null,
      communication: merged.communication || {},
      research_data: merged.research_data || {},
      workExperience: Array.isArray(merged.workExperience) ? merged.workExperience : [],
      education: Array.isArray(merged.education) ? merged.education : [],
      ai_skills: Array.isArray(merged.ai_skills) ? merged.ai_skills : [],
      ai_industries: Array.isArray(merged.ai_industries) ? merged.ai_industries : [],
      phoneNumbers: Array.isArray(merged.phoneNumbers) ? merged.phoneNumbers : []
    };

    // Ensure communication object has proper structure
    if (!safeCandidate.communication.calls) safeCandidate.communication.calls = {};
    if (!safeCandidate.communication.email) safeCandidate.communication.email = {};
    if (!safeCandidate.communication.whatsapp) safeCandidate.communication.whatsapp = {};

    // Ensure history arrays exist
    if (!Array.isArray(safeCandidate.communication.calls.history)) {
      safeCandidate.communication.calls.history = [];
    }
    if (!Array.isArray(safeCandidate.communication.email.history)) {
      safeCandidate.communication.email.history = [];
    }
    if (!Array.isArray(safeCandidate.communication.whatsapp.history)) {
      safeCandidate.communication.whatsapp.history = [];
    }

    setSelectedCandidate(safeCandidate);
    setProfileModalVisible(true);
  };

  // Handle candidate update
  const handleUpdateCandidate = async (values) => {
    try {
      const token = getStoredAuthToken();
      const response = await axios.put(`/api/candidates/${selectedCandidate.id}`, values, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('Candidate updated successfully');
        closeCandidateProfile();
        searchCandidates(); // Refresh the list
      }
    } catch (error) {
      console.error('Update error:', error);
      message.error(error.response?.data?.error || 'Update failed');
    }
  };

  // Handle research candidate
  const handleResearchCandidate = async (candidate) => {
    try {
      setResearchLoading(true);
      const token = getStoredAuthToken();
      const response = await axios.post(`/api/candidates/${candidate.id}/research`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('Research completed successfully');
        // Update candidate data with research results
        setCandidates(prev =>
          prev.map(c =>
            c.id === candidate.id
              ? { ...c, research_data: response.data.research_data }
              : c
          )
        );
        if (selectedCandidate?.id === candidate.id) {
          setSelectedCandidate(prev => ({ ...prev, research_data: response.data.research_data }));
        }
      }
    } catch (error) {
      console.error('Research error:', error);
      message.error(error.response?.data?.error || 'Research failed');
    } finally {
      setResearchLoading(false);
    }
  };

  // Handle email candidate
  const handleEmailCandidate = (candidate) => {
    setEmailCandidate(candidate);
    setEmailModalVisible(true);
  };

  // Handle call candidate
  const handleCallCandidate = (candidate) => {
    setCallCandidate(candidate);
    setCallModalVisible(true);
  };

  // Handle WhatsApp candidate
  const handleWhatsAppCandidate = (candidate) => {
    setWhatsappCandidate(candidate);
    setWhatsappModalVisible(true);
  };

  // Handle evaluate candidate
  const handleEvaluateCandidate = (candidate) => {
    setEvaluateCandidate(candidate);
    setEvaluateModalVisible(true);
  };

  const openAddToCandidateListModal = async (candidate = null) => {
    const candidateIds = candidate?.id ? [candidate.id] : selectedRowKeys;
    if (candidateIds.length === 0) {
      message.warning('Select at least one tenant candidate');
      return;
    }
    setSelectedRowKeys(candidateIds);
    setNewListNameForAdd('');
    setAddToListTargetId(undefined);
    setAddListModalVisible(true);
    try {
      const token = getStoredAuthToken();
      const res = await axios.get('/api/candidate-lists', {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 }
      });
      if (res.data?.success) {
        setCandidateListsForAdd(res.data.lists || []);
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Could not load candidate lists');
    }
  };

  const submitAddToCandidateList = async () => {
    const token = getStoredAuthToken();
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    setAddListSubmitting(true);
    try {
      let listId = addToListTargetId;
      const nameTrim = newListNameForAdd.trim();
      if (nameTrim) {
        const cr = await axios.post('/api/candidate-lists', { name: nameTrim }, { headers });
        if (cr.status === 201 && cr.data?.list_id) {
          listId = cr.data.list_id;
        } else {
          message.error('Failed to create list');
          return;
        }
      }
      if (!listId) {
        message.error('Select an existing list or enter a new list name');
        return;
      }
      await axios.patch(
        `/api/candidate-lists/${encodeURIComponent(listId)}`,
        { add_candidate_ids: selectedRowKeys },
        { headers }
      );
      message.success(`Added ${selectedRowKeys.length} candidate(s) to list`);
      setAddListModalVisible(false);
      setSelectedRowKeys([]);
    } catch (e) {
      message.error(e.response?.data?.error || 'Add to list failed');
    } finally {
      setAddListSubmitting(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      const allowed = new Set(
        (candidates || []).filter(resumeMatchesTenant).map((r) => r.id)
      );
      setSelectedRowKeys(keys.filter((k) => allowed.has(k)));
    },
    getCheckboxProps: (record) => ({
      disabled: !resumeMatchesTenant(record),
    }),
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'contactName',
      key: 'contactName',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            <button onClick={() => viewCandidateProfile(record)} style={{
              background: 'none', border: 'none', padding: 0, margin: 0, color: '#1677ff', cursor: 'pointer'
            }}>{text}</button>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.job_title || record.title || 'No title'}
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => {
        const linkedinLink = record.linkedinUrl || record.linkedin_url || record.linkedin;
        return (
          <div>
            {record.email && (
              <div style={{ fontSize: '12px' }}>
                <MailOutlined /> {record.email}
              </div>
            )}
            {record.phoneNumbers && record.phoneNumbers.length > 0 && (
              <div style={{ fontSize: '12px' }}>
                <PhoneOutlined /> {record.phoneNumbers[0]}
              </div>
            )}
            {linkedinLink && (
              <div style={{ fontSize: '12px' }}>
                <LinkedinOutlined />{' '}
                <a href={linkedinLink} target="_blank" rel="noreferrer">LinkedIn</a>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location) => {
        if (!location) return 'Not specified';

        // Handle location as object with city/country
        if (typeof location === 'object') {
          const city = location.city || '';
          const country = location.country || '';
          return city && country ? `${city}, ${country}` : city || country || 'Not specified';
        }

        // Handle location as string
        return location || 'Not specified';
      },
    },
    {
      title: 'Experience',
      key: 'experience',
      render: (_, record) => {
        const workExp = toArray(record.workExperience);
        return (
          <div>
            {workExp.length > 0 ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>
                  {workExp[0]?.title}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {workExp[0]?.company}
                </div>
                {(workExp[0]?.description || workExp[0]?.summary || workExp[0]?.details || workExp[0]?.content || workExp[0]?.text) && (
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                    {(() => {
                      let descriptionText = '';

                      // Try multiple possible fields for description
                      const desc = workExp[0].description || workExp[0].summary || workExp[0].details || workExp[0].content || workExp[0].text;

                      if (typeof desc === 'string') {
                        descriptionText = desc;
                      } else if (Array.isArray(desc)) {
                        descriptionText = desc[0] || '';
                      } else if (desc && typeof desc === 'object') {
                        // Handle case where description might be an object with text property
                        descriptionText = desc.text || desc.content || desc.description || '';
                      }

                      // Very aggressive truncation - only show first 40 characters
                      return descriptionText.length > 40
                        ? `${descriptionText.substring(0, 40)}...`
                        : descriptionText;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              // Fallback: if no structured work experience, try to show a snippet of the raw workExperience data
              (() => {
                const rawExp = record.workExperience;
                if (typeof rawExp === 'string' && rawExp.length > 0) {
                  return (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>Experience</div>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                        {rawExp.length > 40 ? `${rawExp.substring(0, 40)}...` : rawExp}
                      </div>
                    </div>
                  );
                }
                return 'No experience data';
              })()
            )}
          </div>
        );
      },
    },
    {
      title: 'AI Tags',
      key: 'ai_tags',
      render: (_, record) => {
        const skills = record.ai_skills || [];
        const industries = record.ai_industries || [];
        const experienceLevel = record.ai_experience_level;

        return (
          <div>
            {/* Skills */}
            {skills.length > 0 && (
              <div style={{ marginBottom: '4px' }}>
                <div style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}>Skills:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                  {skills.slice(0, 3).map((skill, index) => (
                    <Tag key={index} size="small" color="blue" style={{ fontSize: '10px' }}>
                      {skill}
                    </Tag>
                  ))}
                  {skills.length > 3 && (
                    <Tag size="small" color="default" style={{ fontSize: '10px' }}>
                      +{skills.length - 3}
                    </Tag>
                  )}
                </div>
              </div>
            )}

            {/* Industries */}
            {industries.length > 0 && (
              <div style={{ marginBottom: '4px' }}>
                <div style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}>Industries:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                  {industries.slice(0, 2).map((industry, index) => (
                    <Tag key={index} size="small" color="green" style={{ fontSize: '10px' }}>
                      {industry}
                    </Tag>
                  ))}
                  {industries.length > 2 && (
                    <Tag size="small" color="default" style={{ fontSize: '10px' }}>
                      +{industries.length - 2}
                    </Tag>
                  )}
                </div>
              </div>
            )}

            {/* Experience Level */}
            {experienceLevel && (
              <div>
                <div style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}>Level:</div>
                <Tag size="small" color="purple" style={{ fontSize: '10px', textTransform: 'capitalize' }}>
                  {experienceLevel}
                </Tag>
              </div>
            )}

            {!skills.length && !industries.length && !experienceLevel && (
              <div style={{ fontSize: '11px', color: '#999' }}>No tags</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Score',
      dataIndex: ['rating', 'score'],
      key: 'score',
      render: (score) => (
        score ? (
          <Tag color={score >= 80 ? 'green' : score >= 60 ? 'orange' : 'red'}>
            {score.toFixed(1)}
          </Tag>
        ) : (
          <Tag color="default">N/A</Tag>
        )
      ),
    },
    {
      title: 'Research',
      key: 'research',
      render: (_, record) => {
        const status = record.research_status;
        const last = record.last_researched;
        const statusTag = status
          ? <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'blue' : 'default'}>{status}</Tag>
          : <Tag color="default">—</Tag>;
        return (
          <div style={{ minWidth: 120 }}>
            <div>{statusTag}</div>
            {last ? (
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                {String(last).replace('T', ' ').replace('Z', '')}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Profile">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewCandidateProfile(record)}
            />
          </Tooltip>
          <Tooltip title="Email">
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => handleEmailCandidate(record)}
            />
          </Tooltip>
          <Tooltip title="Call">
            <Button
              type="text"
              icon={<PhoneOutlined />}
              onClick={() => handleCallCandidate(record)}
            />
          </Tooltip>
          <Tooltip title="WhatsApp">
            <Button
              type="text"
              icon={<MessageOutlined />}
              style={{ color: '#25D366' }}
              onClick={() => handleWhatsAppCandidate(record)}
            />
          </Tooltip>
          <Tooltip title="Research">
            <Button
              type="text"
              icon={<ExperimentOutlined />}
              loading={researchLoading}
              onClick={() => handleResearchCandidate(record)}
            />
          </Tooltip>
          <Tooltip title="Evaluate for Job">
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={() => handleEvaluateCandidate(record)}
            />
          </Tooltip>
          <Tooltip
            title={resumeMatchesTenant(record)
              ? 'Add this candidate to a saved list'
              : 'Shared profiles cannot be added to tenant candidate lists'}
          >
            <Button
              icon={<UnorderedListOutlined />}
              disabled={!resumeMatchesTenant(record)}
              onClick={() => openAddToCandidateListModal(record)}
            >
              Add to list
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Initial search on component mount
  useEffect(() => {
    searchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCandidateSuccess = (newCandidate) => {
    // Refresh the candidate list
    searchCandidates();
    message.success(`Candidate "${newCandidate.contactName}" has been added successfully!`);
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <Title level={2} style={{ marginBottom: '4px' }}>Candidate Search</Title>
          <Text type="secondary">
            Search and manage your internal candidate database
          </Text>
        </div>
        <Space>
          <Button
            icon={<UnorderedListOutlined />}
            onClick={openAddToCandidateListModal}
            disabled={selectedRowKeys.length === 0}
            size="large"
          >
            Add to candidate list ({selectedRowKeys.length})
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddCandidateModalVisible(true)}
            size="large"
          >
            Add Candidate
          </Button>
        </Space>
      </div>

      <Divider />

      {/* Search Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Name"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Job Title"
              value={filters.title}
              onChange={(e) => handleFilterChange('title', e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Industry"
              value={filters.industry}
              onChange={(e) => handleFilterChange('industry', e.target.value)}
              prefix={<FilterOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Position"
              value={filters.position}
              onChange={(e) => handleFilterChange('position', e.target.value)}
              prefix={<FilterOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Location"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              prefix={<FilterOutlined />}
            />
          </Col>
        </Row>

        {/* AI Tag Filters */}
        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple"
              placeholder="Select Skills"
              value={filters.ai_skills}
              onChange={(value) => handleFilterChange('ai_skills', value)}
              style={{ width: '100%' }}
              allowClear
            >
              {skillOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple"
              placeholder="Select Industries"
              value={filters.ai_industries}
              onChange={(value) => handleFilterChange('ai_industries', value)}
              style={{ width: '100%' }}
              allowClear
            >
              {industryOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Experience Level"
              value={filters.ai_experience_level}
              onChange={(value) => handleFilterChange('ai_experience_level', value)}
              style={{ width: '100%' }}
              allowClear
            >
              {experienceLevelOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Min Score"
              value={filters.min_score}
              onChange={(e) => handleFilterChange('min_score', e.target.value)}
              type="number"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Max Score"
              value={filters.max_score}
              onChange={(e) => handleFilterChange('max_score', e.target.value)}
              type="number"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setFilters({
                  name: '',
                  title: '',
                  industry: '',
                  position: '',
                  location: '',
                  min_score: '',
                  max_score: '',
                  ai_skills: [],
                  ai_industries: [],
                  ai_experience_level: '',
                });
                searchCandidates({}, 1);
              }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Results Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={candidates}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          rowSelection={rowSelection}
        />

        {/* Pagination */}
        <div style={{ marginTop: '16px', textAlign: 'right' }}>
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.limit}
            onChange={handlePageChange}
            showSizeChanger
            pageSizeOptions={[20, 50, 100].map(String)}
            onShowSizeChange={handlePageSizeChange}
            showQuickJumper={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} candidates`
            }
          />
        </div>
      </Card>

      <Modal
        title="Add to candidate list"
        open={addListModalVisible}
        onCancel={() => !addListSubmitting && setAddListModalVisible(false)}
        onOk={submitAddToCandidateList}
        confirmLoading={addListSubmitting}
        okText="Add"
      >
        <p style={{ marginBottom: 12 }}>
          Add {selectedRowKeys.length} tenant-owned candidate(s). Universal profiles cannot be added.
        </p>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>Existing list</div>
          <Select
            placeholder="Select a list"
            allowClear
            style={{ width: '100%' }}
            value={addToListTargetId}
            onChange={(v) => setAddToListTargetId(v)}
            options={(candidateListsForAdd || []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <div>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>Or new list name</div>
          <Input
            placeholder="Creates a list then adds selection"
            value={newListNameForAdd}
            onChange={(e) => setNewListNameForAdd(e.target.value)}
          />
        </div>
      </Modal>

      {/* Candidate Profile Modal */}
      <Modal
        title={candidateListReturnTo ? (
          <Space>
            <Button type="link" style={{ padding: 0 }} onClick={closeCandidateProfile}>
              Back to candidate list
            </Button>
            <span>Candidate Profile</span>
          </Space>
        ) : 'Candidate Profile'}
        visible={profileModalVisible}
        onCancel={closeCandidateProfile}
        footer={null}
        width={800}
      >
        {selectedCandidate && (
          <CandidateProfileModal
            candidate={selectedCandidate}
            onUpdate={handleUpdateCandidate}
            onResearch={() => handleResearchCandidate(selectedCandidate)}
            onEmail={() => handleEmailCandidate(selectedCandidate)}
            onCall={() => handleCallCandidate(selectedCandidate)}
            onWhatsApp={() => handleWhatsAppCandidate(selectedCandidate)}
            researchLoading={researchLoading}
          />
        )}
      </Modal>

      {/* Email Modal */}
      <SendCandidateEmailModal
        candidate={emailCandidate}
        isOpen={emailModalVisible}
        onClose={(shouldRefresh) => {
          setEmailModalVisible(false);
          setEmailCandidate(null);
          // Refresh candidate list if email was sent successfully
          if (shouldRefresh) {
            searchCandidates();
          }
        }}
      />

      {/* Call Modal */}
      <SendCandidateCallModal
        visible={callModalVisible}
        candidate={callCandidate}
        onCancel={(shouldRefresh) => {
          setCallModalVisible(false);
          setCallCandidate(null);
          // Refresh candidate list if call was successful
          if (shouldRefresh) {
            searchCandidates();
          }
        }}
      />

      {/* WhatsApp Modal */}
      <SendCandidateWhatsAppModal
        visible={whatsappModalVisible}
        candidate={whatsappCandidate}
        onCancel={() => {
          setWhatsappModalVisible(false);
          setWhatsappCandidate(null);
        }}
        onSuccess={() => {
          // Refresh the candidate list to show updated communication history
          searchCandidates();
        }}
      />

      {/* Add Candidate Modal */}
      <AddCandidateModal
        visible={addCandidateModalVisible}
        onCancel={() => setAddCandidateModalVisible(false)}
        onSuccess={handleAddCandidateSuccess}
      />

      {/* Evaluate Candidate Modal */}
      <EvaluateCandidateModal
        visible={evaluateModalVisible}
        candidate={evaluateCandidate}
        onCancel={() => {
          setEvaluateModalVisible(false);
          setEvaluateCandidate(null);
        }}
        onSuccess={(evaluation, job) => {
          // Refresh candidate list to show updated evaluation scores
          searchCandidates();
        }}
      />
    </div>
  );
};

// Candidate Profile Modal Component
const getCandidateCompensation = (candidate = {}) => (
  candidate.salary_compensation
  ?? candidate.salaryCompensation
  ?? candidate.compensation
  ?? candidate.salary_expectations
  ?? candidate.salaryExpectation
  ?? candidate.expected_salary
  ?? candidate.expectedSalary
  ?? candidate.current_salary
  ?? candidate.currentSalary
  ?? candidate.salary
  ?? ''
);

const formatCandidateCompensation = (value) => {
  if (!value || typeof value !== 'object') return value || '';
  const currency = value.currency || value.currency_code || '';
  const minimum = value.min ?? value.minimum ?? value.from;
  const maximum = value.max ?? value.maximum ?? value.to;
  const period = value.period || value.frequency || '';
  const range = [minimum, maximum]
    .filter((item) => item !== undefined && item !== null && item !== '')
    .join(' - ');
  return [currency, range, period].filter(Boolean).join(' ').trim();
};

const CandidateProfileModal = ({
  candidate,
  onUpdate,
  onResearch,
  onEmail,
  onCall,
  onWhatsApp,
  researchLoading,
}) => {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [tagUpdateLoading, setTagUpdateLoading] = useState(false);
  const [resumeUploadLoading, setResumeUploadLoading] = useState(false);
  const [structuringLoading, setStructuringLoading] = useState(false);

  // Ensure candidate has proper structure to prevent undefined errors
  const safeCandidate = candidate ? {
    ...candidate,
    // Handle both old 'title' and new 'job_title' fields
    job_title: candidate?.job_title || candidate?.title,
    lastEvaluatedJob: candidate?.lastEvaluatedJob || null,
    communication: candidate?.communication || {},
    research_data: candidate?.research_data || {},
    workExperience: Array.isArray(candidate?.workExperience) ? candidate.workExperience : [],
    education: Array.isArray(candidate?.education) ? candidate.education : [],
    ai_skills: Array.isArray(candidate?.ai_skills) ? candidate.ai_skills : [],
    ai_industries: Array.isArray(candidate?.ai_industries) ? candidate.ai_industries : [],
    phoneNumbers: Array.isArray(candidate?.phoneNumbers) ? candidate.phoneNumbers : [],
    salary_compensation: formatCandidateCompensation(getCandidateCompensation(candidate))
  } : {
    communication: {},
    research_data: {},
    workExperience: [],
    education: [],
    ai_skills: [],
    ai_industries: [],
    phoneNumbers: []
  };

  // Ensure communication object has proper structure
  if (!safeCandidate.communication.calls) safeCandidate.communication.calls = {};
  if (!safeCandidate.communication.email) safeCandidate.communication.email = {};
  if (!safeCandidate.communication.whatsapp) safeCandidate.communication.whatsapp = {};

  // Ensure history arrays exist
  if (!Array.isArray(safeCandidate.communication.calls.history)) {
    safeCandidate.communication.calls.history = [];
  }
  if (!Array.isArray(safeCandidate.communication.email.history)) {
    safeCandidate.communication.email.history = [];
  }
  if (!Array.isArray(safeCandidate.communication.whatsapp.history)) {
    safeCandidate.communication.whatsapp.history = [];
  }

  // Ensure research_data arrays are properly initialized
  if (!safeCandidate.research_data) {
    safeCandidate.research_data = {};
  }
  if (!safeCandidate.research_data.contact_info) {
    safeCandidate.research_data.contact_info = {};
  }
  if (!Array.isArray(safeCandidate.research_data.contact_info.emails)) {
    safeCandidate.research_data.contact_info.emails = [];
  }
  if (!Array.isArray(safeCandidate.research_data.contact_info.phone_numbers)) {
    safeCandidate.research_data.contact_info.phone_numbers = [];
  }
  if (!safeCandidate.research_data.skills_analysis) {
    safeCandidate.research_data.skills_analysis = {};
  }
  if (!Array.isArray(safeCandidate.research_data.skills_analysis.current_skills)) {
    safeCandidate.research_data.skills_analysis.current_skills = [];
  }
  if (!Array.isArray(safeCandidate.research_data.skills_analysis.skill_gaps)) {
    safeCandidate.research_data.skills_analysis.skill_gaps = [];
  }

  const [localCandidate, setLocalCandidate] = useState(safeCandidate);

  // Keep modal state in sync when parent candidate changes (e.g. after hydration).
  useEffect(() => {
    setLocalCandidate(safeCandidate);
    form.setFieldsValue(safeCandidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);

  // Predefined options for tag editing (same as main component)
  const skillOptions = [
    // Finance & Banking Skills
    { value: 'Trade Finance', label: 'Trade Finance' },
    { value: 'Investment Banking', label: 'Investment Banking' },
    { value: 'Financial Modeling', label: 'Financial Modeling' },
    { value: 'Corporate Finance', label: 'Corporate Finance' },
    { value: 'Commercial Banking', label: 'Commercial Banking' },
    { value: 'Private Equity', label: 'Private Equity' },
    { value: 'Asset Management', label: 'Asset Management' },
    { value: 'Credit Analysis', label: 'Credit Analysis' },
    { value: 'Mergers & Acquisitions', label: 'Mergers & Acquisitions' },
    { value: 'Compliance', label: 'Compliance' },
    { value: 'Audit', label: 'Audit' },

    // Trading & Risk Management
    { value: 'Oil Trading', label: 'Oil Trading' },
    { value: 'Gas Trading', label: 'Gas Trading' },
    { value: 'Metals Trading', label: 'Metals Trading' },
    { value: 'Agricultural Trading', label: 'Agricultural Trading' },
    { value: 'Hedging', label: 'Hedging' },
    { value: 'Derivatives', label: 'Derivatives' },
    { value: 'Futures', label: 'Futures' },
    { value: 'Options', label: 'Options' },
    { value: 'Var', label: 'VaR (Value at Risk)' },
    { value: 'Risk Management', label: 'Risk Management' },
    { value: 'Stress Testing', label: 'Stress Testing' },

    // Supply Chain & Logistics
    { value: 'Supply Chain Finance', label: 'Supply Chain Finance' },
    { value: 'Supply Chain Management', label: 'Supply Chain Management' },
    { value: 'Logistics Management', label: 'Logistics Management' },
    { value: 'Warehouse Management', label: 'Warehouse Management' },
    { value: 'Inventory Management', label: 'Inventory Management' },
    { value: 'Freight Forwarding', label: 'Freight Forwarding' },
    { value: 'Customs Clearance', label: 'Customs Clearance' },

    // Food & Beverage & Hospitality
    { value: 'Food And Beverage', label: 'Food & Beverage' },
    { value: 'Restaurant Management', label: 'Restaurant Management' },
    { value: 'Hotel Management', label: 'Hotel Management' },
    { value: 'Hospitality Management', label: 'Hospitality Management' },
    { value: 'Catering', label: 'Catering' },
    { value: 'Food Safety', label: 'Food Safety' },
    { value: 'HACCP', label: 'HACCP' },
    { value: 'Food Quality Control', label: 'Food Quality Control' },
    { value: 'Culinary Arts', label: 'Culinary Arts' },

    // Technology Skills
    { value: 'Python', label: 'Python' },
    { value: 'JavaScript', label: 'JavaScript' },
    { value: 'Java', label: 'Java' },
    { value: 'SQL', label: 'SQL' },
    { value: 'React', label: 'React' },
    { value: 'Node.js', label: 'Node.js' },
    { value: 'AWS', label: 'AWS' },
    { value: 'Docker', label: 'Docker' },
    { value: 'Kubernetes', label: 'Kubernetes' },
    { value: 'R', label: 'R Programming' },
    { value: 'Go', label: 'Go Programming' },
    { value: 'Machine Learning', label: 'Machine Learning' },
    { value: 'Data Science', label: 'Data Science' },
    { value: 'Data Analysis', label: 'Data Analysis' },

    // Commodity Specific
    { value: 'Crude Oil', label: 'Crude Oil' },
    { value: 'Natural Gas', label: 'Natural Gas' },
    { value: 'Copper', label: 'Copper' },
    { value: 'Aluminum', label: 'Aluminum' },
    { value: 'Gold', label: 'Gold' },
    { value: 'Silver', label: 'Silver' },
    { value: 'Corn', label: 'Corn' },
    { value: 'Wheat', label: 'Wheat' },
    { value: 'Soybeans', label: 'Soybeans' },
    { value: 'Cotton', label: 'Cotton' },
    { value: 'Sugar', label: 'Sugar' },
    { value: 'Coffee', label: 'Coffee' },
    { value: 'Cocoa', label: 'Cocoa' },

    // Trading Strategies
    { value: 'Arbitrage', label: 'Arbitrage' },
    { value: 'Spread Trading', label: 'Spread Trading' },
    { value: 'Pairs Trading', label: 'Pairs Trading' },
    { value: 'Algorithmic Trading', label: 'Algorithmic Trading' },
    { value: 'Quantitative Trading', label: 'Quantitative Trading' },
    { value: 'High Frequency Trading', label: 'High Frequency Trading' },

    // Market Analysis
    { value: 'Technical Analysis', label: 'Technical Analysis' },
    { value: 'Fundamental Analysis', label: 'Fundamental Analysis' },
    { value: 'Supply & Demand Analysis', label: 'Supply & Demand Analysis' },
    { value: 'Market Structure Analysis', label: 'Market Structure Analysis' },
    { value: 'Geopolitical Analysis', label: 'Geopolitical Analysis' },
    { value: 'Weather Analysis', label: 'Weather Analysis' },
  ];

  const industryOptions = [
    // Core Industries
    { value: 'Finance', label: 'Finance' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Education', label: 'Education' },
    { value: 'Consulting', label: 'Consulting' },

    // Specialized Finance & Trading
    { value: 'Commodity Finance', label: 'Commodity Finance' },
    { value: 'Commodity Trading', label: 'Commodity Trading' },
    { value: 'Investment Banking', label: 'Investment Banking' },
    { value: 'Commercial Banking', label: 'Commercial Banking' },

    // Food & Beverage & Hospitality
    { value: 'F&B Hospitality', label: 'F&B & Hospitality' },
    { value: 'Food Processing', label: 'Food Processing' },

    // Energy & Resources
    { value: 'Energy', label: 'Energy' },
    { value: 'Mining & Metals', label: 'Mining & Metals' },
    { value: 'Agriculture', label: 'Agriculture' },

    // Logistics & Supply Chain
    { value: 'Logistics', label: 'Logistics' },

    // Other Industries
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'E-commerce', label: 'E-commerce' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Media', label: 'Media' },
  ];

  const experienceLevelOptions = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'lead', label: 'Lead' },
    { value: 'executive', label: 'Executive' },
  ];

  useEffect(() => {
    if (candidate) {
      setLocalCandidate(candidate);
      const safeWorkExp = Array.isArray(candidate.workExperience)
        ? candidate.workExperience
        : (candidate.workExperience ? [candidate.workExperience] : []);

      // Handle location as object or string
      let locationValue = candidate.location;
      if (typeof locationValue === 'object' && locationValue !== null) {
        const city = locationValue.city || '';
        const country = locationValue.country || '';
        locationValue = city && country ? `${city}, ${country}` : city || country || '';
      }

      form.setFieldsValue({
        contactName: candidate.contactName,
        email: candidate.email,
        phoneNumbers: Array.isArray(candidate.phoneNumbers)
          ? candidate.phoneNumbers.join(', ')
          : (candidate.phoneNumbers || ''),
        job_title: candidate.job_title || candidate.title,
        location: locationValue,
        company: candidate.company,
        linkedinUrl: candidate.linkedinUrl || candidate.linkedin_url || '',
        salary_compensation: formatCandidateCompensation(getCandidateCompensation(candidate)),
        workExperience: safeWorkExp,
      });
    }
  }, [candidate, form]);

  const handleSubmit = async (values) => {
    const phoneRaw = values.phoneNumbers;
    const phoneList = Array.isArray(phoneRaw)
      ? phoneRaw.map((p) => String(p).trim()).filter(Boolean)
      : (typeof phoneRaw === 'string'
        ? phoneRaw.split(',').map((p) => p.trim()).filter(Boolean)
        : []);
    const updateData = {
      ...values,
      phoneNumbers: phoneList,
      linkedinUrl: values.linkedinUrl,
      workExperience: (Array.isArray(values.workExperience) ? values.workExperience : []).map(exp => ({
        title: exp.title || '',
        company: exp.company || '',
        starts_at: exp.starts_at || '',
        ends_at: exp.ends_at || '',
        location: exp.location || '',
        description: exp.description || '',
      })),
    };
    await onUpdate(updateData);
    setEditing(false);
  };

  const handleUpdateTags = async () => {
    try {
      setTagUpdateLoading(true);
      const token = getStoredAuthToken();

      const response = await axios.put(`/api/candidates/${candidate.id}/tags`, {
        ai_skills: localCandidate.ai_skills || [],
        ai_industries: localCandidate.ai_industries || [],
        ai_experience_level: localCandidate.ai_experience_level || '',
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('Tags updated successfully');
        setEditingTags(false);
        // Refresh the candidate list to show updated tags
        window.location.reload(); // Simple refresh for now
      } else {
        message.error('Failed to update tags');
      }
    } catch (error) {
      console.error('Tag update error:', error);
      message.error(error.response?.data?.error || 'Failed to update tags');
    } finally {
      setTagUpdateLoading(false);
    }
  };

  // Don't render if candidate is not provided
  if (!candidate) {
    return null;
  }

  return (
    <div>
      <div style={{ marginBottom: '16px', textAlign: 'right' }}>
        <Space wrap>
          <Tooltip title="Send email">
            <Button
              icon={<MailOutlined />}
              onClick={onEmail}
            >
              Email
            </Button>
          </Tooltip>
          <Tooltip title="Send WhatsApp message">
            <Button
              icon={<MessageOutlined />}
              style={{ color: '#25D366', borderColor: '#25D366' }}
              onClick={onWhatsApp}
            >
              WhatsApp
            </Button>
          </Tooltip>
          <Tooltip title="Call candidate">
            <Button
              icon={<PhoneOutlined />}
              onClick={onCall}
            >
              Call
            </Button>
          </Tooltip>
          <Button
            icon={<ExperimentOutlined />}
            loading={researchLoading}
            onClick={onResearch}
          >
            Research
          </Button>
          <Upload
            accept=".pdf,.docx"
            maxCount={1}
            showUploadList={false}
            customRequest={async ({ file, onError, onSuccess }) => {
              setResumeUploadLoading(true);
              try {
                const token = getStoredAuthToken();
                const formData = new FormData();
                formData.append('resume', file);
                const res = await axios.post(
                  `/api/candidates/${encodeURIComponent(candidate.id)}/resume`,
                  formData,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data',
                    },
                  }
                );
                if (res.data?.success) {
                  message.success('Resume uploaded. Embedding regenerated.');
                  // Refresh candidate snapshot inside modal
                  try {
                    const refreshed = await axios.get(
                      `/api/candidates/${encodeURIComponent(candidate.id)}`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (refreshed.data?.success && refreshed.data?.candidate) {
                      const merged = { ...candidate, ...refreshed.data.candidate };
                      setLocalCandidate(merged);
                      form.setFieldsValue(merged);
                    }
                  } catch (_) {
                    // No-op: upload succeeded; modal can be reopened to see full refresh
                  }
                  onSuccess?.('ok');
                } else {
                  throw new Error(res.data?.error || 'Upload failed');
                }
              } catch (e) {
                message.error(e.response?.data?.error || e.message || 'Upload failed');
                onError?.(e);
              } finally {
                setResumeUploadLoading(false);
              }
            }}
          >
            <Button loading={resumeUploadLoading}>Upload resume</Button>
          </Upload>
          <Popconfirm
            title="Extract & update profile?"
            description="Updates only work experience and job title from the uploaded resume text."
            okText="Extract"
            onConfirm={async () => {
              setStructuringLoading(true);
              try {
                const token = getStoredAuthToken();
                const res = await axios.post(
                  `/api/candidates/${encodeURIComponent(candidate.id)}/extract-profile-from-resume`,
                  {},
                  { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                );
                if (res.data?.success) {
                  message.success(`Updated: ${res.data.workExperience_count || 0} roles`);
                  const refreshed = await axios.get(
                    `/api/candidates/${encodeURIComponent(candidate.id)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (refreshed.data?.success && refreshed.data?.candidate) {
                    const merged = { ...candidate, ...refreshed.data.candidate };
                    setLocalCandidate(merged);
                    form.setFieldsValue(merged);
                  }
                } else {
                  message.error(res.data?.error || 'Extraction failed');
                }
              } catch (e) {
                message.error(e.response?.data?.error || 'Extraction failed');
              } finally {
                setStructuringLoading(false);
              }
            }}
          >
            <Button loading={structuringLoading}>Extract & update</Button>
          </Popconfirm>
          <Button
            icon={<EditOutlined />}
            onClick={() => setEditing(!editing)}
          >
            {editing ? 'Cancel Edit' : 'Edit'}
          </Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={!editing}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Name" name="contactName">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Email" name="email">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Phone Numbers" name="phoneNumbers">
              <Input placeholder="Separate multiple numbers with commas" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Job Title" name="job_title">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Location" name="location">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Company" name="company">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="LinkedIn URL" name="linkedinUrl">
              <Input placeholder="https://www.linkedin.com/in/..." />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Salary / Compensation" name="salary_compensation">
              <Input placeholder="e.g. SGD 180,000 base + bonus" />
            </Form.Item>
          </Col>
        </Row>

        {editing && (
          <div style={{ marginTop: '8px' }}>
            <Title level={5}>Work Experience</Title>
            <Form.List name="workExperience">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} style={{ marginBottom: '8px' }} size="small">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, 'title']} label="Title">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, 'company']} label="Company">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, 'starts_at']} label="Start">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, 'ends_at']} label="End">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, 'location']} label="Location">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item {...restField} name={[name, 'description']} label="Description">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                      <div style={{ textAlign: 'right' }}>
                        <Button danger onClick={() => remove(name)}>Remove</Button>
                      </div>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>
                    Add Experience
                  </Button>
                </>
              )}
            </Form.List>
          </div>
        )}

        {editing && (
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </Form.Item>
        )}
      </Form>

      {/* AI Tags Section */}
      <div style={{ marginTop: '24px' }}>
        <Title level={4}>
          AI-Generated Tags
          {editing && (
            <Button
              type="link"
              size="small"
              style={{ marginLeft: '8px' }}
              onClick={() => setEditingTags(!editingTags)}
            >
              {editingTags ? 'Cancel Edit' : 'Edit Tags'}
            </Button>
          )}
        </Title>
        <Card>
          {editingTags ? (
            <Form layout="vertical">
              {/* Skills Editing */}
              <Form.Item label="Skills">
                <Select
                  mode="tags"
                  placeholder="Add or remove skills"
                  value={localCandidate.ai_skills || []}
                  onChange={(value) => {
                    setLocalCandidate(prev => ({
                      ...prev,
                      ai_skills: value
                    }));
                  }}
                  style={{ width: '100%' }}
                >
                  {skillOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Industries Editing */}
              <Form.Item label="Industries">
                <Select
                  mode="tags"
                  placeholder="Add or remove industries"
                  value={localCandidate.ai_industries || []}
                  onChange={(value) => {
                    setLocalCandidate(prev => ({
                      ...prev,
                      ai_industries: value
                    }));
                  }}
                  style={{ width: '100%' }}
                >
                  {industryOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Experience Level Editing */}
              <Form.Item label="Experience Level">
                <Select
                  placeholder="Select experience level"
                  value={localCandidate.ai_experience_level || ''}
                  onChange={(value) => {
                    setLocalCandidate(prev => ({
                      ...prev,
                      ai_experience_level: value
                    }));
                  }}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {experienceLevelOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    onClick={handleUpdateTags}
                    loading={tagUpdateLoading}
                  >
                    Save Tags
                  </Button>
                  <Button onClick={() => setEditingTags(false)}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <>
              {/* Skills Display */}
              {localCandidate.ai_skills?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Skills:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {localCandidate.ai_skills.map((skill, index) => (
                      <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
                        {skill}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Industries Display */}
              {localCandidate.ai_industries?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Industries:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {localCandidate.ai_industries.map((industry, index) => (
                      <Tag key={index} color="green" style={{ marginBottom: '4px' }}>
                        {industry}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Level Display */}
              {localCandidate.ai_experience_level && (
                <div>
                  <Text strong>Experience Level:</Text>
                  <div style={{ marginTop: '8px' }}>
                    <Tag color="purple" style={{ textTransform: 'capitalize' }}>
                      {localCandidate.ai_experience_level}
                    </Tag>
                  </div>
                </div>
              )}

              {!localCandidate.ai_skills?.length && !localCandidate.ai_industries?.length && !localCandidate.ai_experience_level && (
                <div style={{ color: '#999', fontStyle: 'italic' }}>
                  No AI tags available. Click "Edit Tags" to add them manually.
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Work Experience Section */}
      {Array.isArray(safeCandidate.workExperience) && safeCandidate.workExperience.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <Title level={4}>Work Experience</Title>
          {safeCandidate.workExperience.map((exp, index) => (
            <Card key={index} style={{ marginBottom: '8px' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{exp.title}</div>
                <div style={{ color: '#666' }}>{exp.company}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {exp.starts_at} - {exp.ends_at || 'Present'} | {
                    (() => {
                      const location = exp.location;
                      if (!location) return 'No location';

                      // Handle location as object with city/country
                      if (typeof location === 'object') {
                        const city = location.city || '';
                        const country = location.country || '';
                        return city && country ? `${city}, ${country}` : city || country || 'No location';
                      }

                      // Handle location as string
                      return location;
                    })()
                  }
                </div>
                {exp.description && (
                  <div style={{ marginTop: '8px' }}>
                    {Array.isArray(exp.description)
                      ? exp.description.map((desc, i) => <div key={i}>• {desc}</div>)
                      : exp.description
                    }
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Research Data Section */}
      {safeCandidate.research_data && (
        <div style={{ marginTop: '24px' }}>
          <Title level={4}>Research Data</Title>
          <Card>
            {safeCandidate.research_data.contact_info && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Additional Contact Info:</Text>
                <div>
                  {Array.isArray(safeCandidate.research_data.contact_info.emails) &&
                    safeCandidate.research_data.contact_info.emails.map((email, i) => (
                    <Tag key={i} color="blue">{email}</Tag>
                  ))}
                  {Array.isArray(safeCandidate.research_data.contact_info.phone_numbers) &&
                    safeCandidate.research_data.contact_info.phone_numbers.map((phone, i) => (
                    <Tag key={i} color="green">{phone}</Tag>
                  ))}
                </div>
              </div>
            )}

            {safeCandidate.research_data.skills_analysis && (
              <div>
                <Text strong>Skills Analysis:</Text>
                <div style={{ marginTop: '8px' }}>
                  {Array.isArray(safeCandidate.research_data.skills_analysis.current_skills) &&
                    safeCandidate.research_data.skills_analysis.current_skills.map((skill, i) => (
                    <Tag key={i} color="blue">{skill}</Tag>
                  ))}
                  {Array.isArray(safeCandidate.research_data.skills_analysis.skill_gaps) &&
                    safeCandidate.research_data.skills_analysis.skill_gaps.map((skill, i) => (
                    <Tag key={i} color="orange">{skill}</Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Communication Logs Section */}
      <div style={{ marginTop: '24px' }}>
        <Title level={4}>Communication History</Title>

        {/* Call History */}
        {safeCandidate.communication?.calls?.history?.length > 0 && (
          <Card style={{ marginBottom: '16px' }}>
            <Title level={5}>📞 Call History</Title>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {(safeCandidate.communication?.calls?.history || [])
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((call, index) => (
                  <div key={index} style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <Text strong>Call #{index + 1}</Text>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {new Date(call.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Tag color={
                          call.status === 'completed' ? 'green' :
                          call.status === 'missed' ? 'red' :
                          call.status === 'scheduled' ? 'orange' :
                          'default'
                        }>
                          {call.status || 'Unknown'}
                        </Tag>
                        {call.duration && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Duration: {call.duration} min
                          </div>
                        )}
                      </div>
                    </div>

                    {call.jobId && (
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        Job ID: {call.jobId}
                      </div>
                    )}

                    {call.notes && (
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        Notes: {call.notes}
                      </div>
                    )}

                    {/* Recording Player */}
                    {call.recordingUrl && (
                      <div style={{ marginTop: '12px' }}>
                        <Text strong style={{ fontSize: '14px' }}>Recording:</Text>
                        <div style={{ marginTop: '8px' }}>
                          <RecordingPlayer audioUrl={call.recordingUrl} />
                        </div>
                      </div>
                    )}

                    {/* Debug Info - Remove this after testing */}
                    <div style={{ marginTop: '8px', fontSize: '10px', color: '#999' }}>
                      Debug: recordingUrl = {call.recordingUrl ? 'EXISTS' : 'MISSING'} |
                      call_id = {call.call_id || 'N/A'} |
                      status = {call.status || 'N/A'}
                    </div>

                    {/* Transcript */}
                    {call.transcription && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <Text strong style={{ fontSize: '14px' }}>Transcript:</Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(call.transcription);
                              message.success('Transcript copied to clipboard');
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                        <div style={{
                          backgroundColor: '#f5f5f5',
                          padding: '12px',
                          borderRadius: '6px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          fontSize: '12px',
                          lineHeight: '1.4'
                        }}>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                            {call.transcription}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </Card>
        )}

        {/* Email History */}
        {safeCandidate.communication?.email?.history?.length > 0 && (
          <Card style={{ marginBottom: '16px' }}>
            <Title level={5}>📧 Email History</Title>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {(safeCandidate.communication?.email?.history || [])
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((email, index) => (
                  <div key={index} style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <Text strong>Email #{index + 1}</Text>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {new Date(email.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <Tag color={
                        email.status === 'sent' ? 'blue' :
                        email.status === 'delivered' ? 'green' :
                        email.status === 'opened' ? 'orange' :
                        email.status === 'replied' ? 'purple' :
                        'default'
                      }>
                        {email.status || 'Unknown'}
                      </Tag>
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Subject:</Text> {email.subject}
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>To:</Text> {email.recipient}
                    </div>

                    {email.jobId && (
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        Job ID: {email.jobId}
                      </div>
                    )}

                    {/* Email Content Preview */}
                    <div style={{ marginTop: '12px' }}>
                      <Text strong style={{ fontSize: '14px' }}>Content:</Text>
                      <div style={{
                        backgroundColor: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '6px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        fontSize: '12px',
                        lineHeight: '1.4',
                        marginTop: '8px'
                      }}>
                        {email.content && email.content.length > 200
                          ? `${email.content.substring(0, 200)}...`
                          : (email.content || '')
                        }
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        )}

        {/* WhatsApp History */}
        {safeCandidate.communication?.whatsapp?.history?.length > 0 && (
          <Card style={{ marginBottom: '16px' }}>
            <Title level={5}>💬 WhatsApp History</Title>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {(safeCandidate.communication?.whatsapp?.history || [])
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((whatsapp, index) => (
                  <div key={index} style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <Text strong>WhatsApp #{index + 1}</Text>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {new Date(whatsapp.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <Tag color={
                        whatsapp.status === 'sent' ? 'blue' :
                        whatsapp.status === 'delivered' ? 'green' :
                        whatsapp.status === 'read' ? 'purple' :
                        whatsapp.status === 'failed' ? 'red' :
                        'default'
                      }>
                        {whatsapp.status || 'Unknown'}
                      </Tag>
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>To:</Text> {whatsapp.phoneNumber}
                    </div>

                    {whatsapp.jobId && (
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        Job ID: {whatsapp.jobId}
                      </div>
                    )}

                    {/* WhatsApp Content Preview */}
                    <div style={{ marginTop: '12px' }}>
                      <Text strong style={{ fontSize: '14px' }}>Message:</Text>
                      <div style={{
                        backgroundColor: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '6px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        fontSize: '12px',
                        lineHeight: '1.4',
                        marginTop: '8px'
                      }}>
                        {whatsapp.content && whatsapp.content.length > 200
                          ? `${whatsapp.content.substring(0, 200)}...`
                          : (whatsapp.content || '')
                        }
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        )}

        {/* Communication Statistics */}
        {safeCandidate.communication?.stats && (
          <Card>
            <Title level={5}>📊 Communication Statistics</Title>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Interactions"
                  value={safeCandidate.communication.stats.totalInteractions || 0}
                  prefix={<MessageOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Calls"
                  value={safeCandidate.communication.calls?.totalCalls || 0}
                  prefix={<PhoneOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Emails"
                  value={safeCandidate.communication.email?.totalEmails || 0}
                  prefix={<MailOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total WhatsApp"
                  value={safeCandidate.communication.whatsapp?.totalMessages || 0}
                  prefix={<MessageOutlined />}
                />
              </Col>
            </Row>
            {safeCandidate.communication.stats.lastContactDate && (
              <div style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
                Last Contact: {new Date(safeCandidate.communication.stats.lastContactDate).toLocaleString()}
              </div>
            )}
          </Card>
        )}

        <div style={{ marginTop: '24px' }}>
          <PlacementJourneyPanel candidate={localCandidate} />
        </div>

        {/* No Communication History */}
        {(!safeCandidate.communication?.calls?.history?.length || safeCandidate.communication.calls.history.length === 0) &&
         (!safeCandidate.communication?.email?.history?.length || safeCandidate.communication.email.history.length === 0) &&
         (!safeCandidate.communication?.whatsapp?.history?.length || safeCandidate.communication.whatsapp.history.length === 0) && (
          <Card>
            <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
              <MessageOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
              <div>No communication history available</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Calls, emails, and WhatsApp messages will appear here once initiated
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CandidateSearch;
